import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { BookOpenCheck, CircleCheck, ScanLine, Sparkles, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { fileteDasAbas } from '@/componentes/filete';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarDataHora, formatarInteiro } from '@/lib/formato';
import { explicarFalha, pedir } from '@/lib/pedido';
import { useSessao } from '@/modulos/sessao/consultas';

import { ConjuntoDeAvaliacao, DetalheDaRodada } from './ConjuntoDeAvaliacao';
import {
  ASSUNTOS_DA_IA,
  PERIODOS,
  porcento,
  ROTULO_DO_ASSUNTO,
  totais,
  validarBuscaDaQualidade,
  type AssuntoDaIa,
  type LeituraDeFolhaNoPeriodo,
  type LinhaPorAssunto,
  type Periodo,
  type Placar,
  taxaOuNada,
} from './qualidadeDaIa';

const rota = getRouteApi('/painel/ia');

interface Qualidade {
  readonly dias: number;
  readonly porAssunto: readonly LinhaPorAssunto[];
  readonly folhas: readonly LeituraDeFolhaNoPeriodo[];
  readonly avaliacao: { readonly rodadaId: string; readonly concluidaEm: string; readonly placar: Placar } | null;
}

interface ItemDoRegistro {
  readonly id: string;
  readonly quando: string;
  readonly assunto: AssuntoDaIa;
  readonly quem: string | null;
  readonly equipamento: string | null;
  readonly pergunta: string;
  readonly sabe: boolean;
  readonly resposta: string;
  readonly citacoes: number;
  readonly falta: string | null;
  readonly modelo: string | null;
  readonly tokensEntrada: number | null;
  readonly tokensSaida: number | null;
  readonly duracaoMs: number | null;
  readonly util: boolean | null;
  readonly motivoDaNota: string | null;
}

interface Registro {
  readonly itens: readonly ItemDoRegistro[];
  readonly pagina: number;
  readonly porPagina: number;
  readonly total: number;
}

function contextoDasRespostas(dias: number, naoSei: number, folhas: number): string {
  return `nos últimos ${String(dias)} dias · ${formatarInteiro(naoSei)} “não sei”, a IA sem base para responder · ${formatarInteiro(folhas)} ${folhas === 1 ? 'folha lida' : 'folhas lidas'}`;
}

/** A meta inicial do guia (6.3) para a utilidade e para a fundamentação. */
const META_DE_UTILIDADE = 0.8;

/**
 * Etapa C — a qualidade da IA (guia 6.3, 11.1 e 15.1).
 *
 * Em cima, o que o guia mede e o registro já sabe responder: fundamentação,
 * utilidade, o acerto da leitura de folha e o custo. Embaixo, o registro de
 * toda saída da IA, com a nota de quem pediu. Precisão, abstenção e
 * recuperação pedem o gabarito do conjunto de avaliação (15.1), e a tela diz
 * isso em vez de fazer conta com o "não sei" do dia a dia.
 */
export function PaginaQualidadeDaIa() {
  const sessao = useSessao();
  const pode = (sessao.data?.permissoes ?? []).includes('ia.auditar');
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const dias: Periodo = busca.dias ?? 30;
  const assunto = busca.assunto;
  const pagina = busca.pagina ?? 1;
  const aba = busca.aba ?? 'registro';
  const [aberto, setAberto] = useState<string | null>(null);
  const [rodadaAberta, setRodadaAberta] = useState<string | null>(null);

  const qualidade = useQuery({
    queryKey: ['ia', 'qualidade', dias],
    enabled: pode,
    queryFn: () => pedir<Qualidade>(`/ia/qualidade?dias=${String(dias)}`),
    meta: { oQueLia: 'a qualidade da IA' },
  });
  const registro = useQuery({
    queryKey: ['ia', 'registro', assunto ?? '', pagina],
    enabled: pode,
    queryFn: () => pedir<Registro>(`/ia/registro?pagina=${String(pagina)}${assunto ? `&assunto=${assunto}` : ''}`),
    meta: { oQueLia: 'o registro da IA' },
  });

  function irPara(estado: { readonly dias?: Periodo; readonly assunto?: AssuntoDaIa | ''; readonly pagina?: number; readonly aba?: 'registro' | 'avaliacao' }) {
    const proximoAssunto = 'assunto' in estado ? estado.assunto : assunto;
    void navegar({
      search: validarBuscaDaQualidade({
        aba: estado.aba ?? aba,
        dias: estado.dias ?? dias,
        ...(proximoAssunto ? { assunto: proximoAssunto } : {}),
        pagina: estado.pagina ?? 1,
      }),
    });
  }

  if (sessao.data && !pode) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina sobretitulo="Painel" titulo="Qualidade da IA" />
          <EstadoVazio
            icone={Sparkles}
            titulo="Você não tem acesso à qualidade da IA"
            descricao="O registro junta as respostas da IA de todos os módulos. Peça ao administrador da empresa a permissão de auditar a IA, que vem no perfil Planejador de manutenção."
          />
        </div>
      </div>
    );
  }

  const t = qualidade.data ? totais(qualidade.data.porAssunto, qualidade.data.folhas) : null;
  const itens = registro.data?.itens ?? [];
  const total = registro.data?.total ?? 0;
  const porPagina = registro.data?.porPagina ?? 100;
  const paginas = Math.max(Math.ceil(total / porPagina), 1);
  const item = itens.find((i) => i.id === aberto) ?? null;

  const chips: readonly ChipFiltro[] = assunto
    ? [{ id: 'assunto', rotulo: `Assunto: ${ROTULO_DO_ASSUNTO[assunto]}`, aoRemover: () => { irPara({ assunto: '' }); } }]
    : [];

  const colunas: readonly Coluna<ItemDoRegistro>[] = [
    {
      chave: 'quando',
      rotulo: 'Quando',
      prioridade: 'chave',
      celula: (i) => (
        <span className="flex flex-col">
          <span className="txt-corpo whitespace-nowrap text-tinta tabular-nums">{formatarDataHora(new Date(i.quando))}</span>
          <TextoDaCelula texto={i.quem ?? 'Fora do cadastro'} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'assunto',
      rotulo: 'Saída da IA',
      celula: (i) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{ROTULO_DO_ASSUNTO[i.assunto]}</span>
          <TextoDaCelula texto={i.equipamento ? `${i.equipamento} · ${i.pergunta}` : i.pergunta} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'resultado',
      rotulo: 'Resultado',
      celula: (i) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">
            {i.assunto === 'folha' || i.assunto === 'folha_de_turno'
              ? 'Leitura do papel'
              : i.sabe
                ? i.citacoes > 0
                  ? `Respondeu · ${formatarInteiro(i.citacoes)} ${i.citacoes === 1 ? 'citação' : 'citações'}`
                  : 'Respondeu'
                : 'Não sabia'}
          </span>
          <TextoDaCelula texto={i.resposta} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'nota',
      rotulo: 'Serviu?',
      prioridade: 'secundaria',
      celula: (i) => (i.util === null ? <span className="text-tinta-secundaria">Sem nota</span> : i.util ? 'Serviu' : 'Não serviu'),
    },
    {
      chave: 'tokens',
      rotulo: 'Tokens',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (i) =>
        i.tokensEntrada === null ? <span className="text-tinta-secundaria">Sem registro</span> : formatarInteiro(i.tokensEntrada + (i.tokensSaida ?? 0)),
    },
  ];

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Painel"
            titulo="Qualidade da IA"
            subtitulo="O que a IA respondeu, se serviu, quanto acertou na leitura do papel e quanto custou"
            acoes={
              <Seletor<`${Periodo}`>
                rotulo="Período"
                valor={`${dias}`}
                opcoes={PERIODOS.map((d) => ({ valor: `${d}` as `${Periodo}`, rotulo: `${String(d)} dias` }))}
                aoEscolher={(v) => {
                  irPara({ dias: Number(v) as Periodo, pagina: pagina });
                }}
              />
            }
          />

          {qualidade.isError ? (
            <EstadoVazio
              tom="erro"
              titulo="Não foi possível medir a IA"
              descricao={`${explicarFalha(qualidade.error).erro}. ${explicarFalha(qualidade.error).comoResolver}`}
              acao={<Botao nivel="secundario" onClick={() => { void qualidade.refetch(); }}>Medir de novo</Botao>}
            />
          ) : (
            <FaixaIndicadores>
              <CardIndicador
                carregando={!t}
                icone={Sparkles}
                tom="disponivel"
                rotulo="Respostas da IA"
                valor={t ? formatarInteiro(t.respostas) : ''}
                contexto={t && qualidade.data ? contextoDasRespostas(dias, t.naoSei, qualidade.data.folhas.reduce((n, f) => n + f.lidas, 0)) : ''}
              />
              <CardIndicador
                carregando={!t}
                icone={CircleCheck}
                tom="operando"
                rotulo="Serviu a quem pediu"
                valor={t?.utilidade === null || !t ? 'Sem nota' : porcento(t.utilidade)}
                alerta={t !== null && t.utilidade !== null && t.utilidade < META_DE_UTILIDADE}
                contexto={t ? (t.avaliadas === 0 ? 'Ninguém avaliou ainda. A meta do guia é 80%.' : `de ${formatarInteiro(t.avaliadas)} ${t.avaliadas === 1 ? 'resposta avaliada' : 'respostas avaliadas'} · meta do guia: 80%`) : ''}
                {...(t?.utilidade !== null && t?.utilidade !== undefined ? { proporcao: t.utilidade } : {})}
              />
              <CardIndicador
                carregando={!t}
                icone={BookOpenCheck}
                tom="marca"
                rotulo="Respostas com fonte"
                valor={t?.fundamentacao === null || !t ? 'Sem resposta' : porcento(t.fundamentacao)}
                contexto="das respostas técnicas, as que citam documento ou fato · meta do guia: 100%"
              />
              <CardIndicador
                carregando={!t}
                icone={ScanLine}
                tom="standby"
                rotulo="Acerto na leitura do papel"
                valor={t?.acertoDaLeitura === null || !t ? 'Sem conferência' : porcento(t.acertoDaLeitura)}
                contexto={t ? (t.camposLidos === 0 ? 'Nenhuma folha conferida no período.' : `dos ${formatarInteiro(t.camposLidos)} campos conferidos, os que ninguém precisou corrigir`) : ''}
              />
            </FaixaIndicadores>
          )}

          {qualidade.data?.avaliacao ? (
            <>
              <p className="txt-apoio text-tinta-secundaria">
                {`Contra o gabarito — a última rodada do conjunto de avaliação, em ${formatarDataHora(new Date(qualidade.data.avaliacao.concluidaEm))}, com ${formatarInteiro(qualidade.data.avaliacao.placar.casos)} ${qualidade.data.avaliacao.placar.casos === 1 ? 'caso' : 'casos'} · custo do período: ${formatarInteiro(t?.tokens ?? 0)} tokens`}
              </p>
              <FaixaIndicadores>
                <CardIndicador icone={CircleCheck} tom="operando" rotulo="Precisão" valor={taxaOuNada(qualidade.data.avaliacao.placar.precisao)} alerta={(qualidade.data.avaliacao.placar.precisao ?? 1) < 0.9} contexto="respostas certas nos casos de responder · meta do guia: 90%" />
                <CardIndicador icone={Sparkles} tom="disponivel" rotulo="Abstenção certa" valor={taxaOuNada(qualidade.data.avaliacao.placar.abstencao)} alerta={(qualidade.data.avaliacao.placar.abstencao ?? 1) < 0.95} contexto="“não sei” quando o manual não responde · meta do guia: 95%" />
                <CardIndicador icone={BookOpenCheck} tom="marca" rotulo="Recuperação" valor={taxaOuNada(qualidade.data.avaliacao.placar.recuperacao)} alerta={(qualidade.data.avaliacao.placar.recuperacao ?? 1) < 0.9} contexto="o trecho certo entre os achados pela busca · meta do guia: 90%" />
                <CardIndicador icone={TriangleAlert} tom="parada" rotulo="Disse o proibido" valor={formatarInteiro(qualidade.data.avaliacao.placar.violacoes)} alerta={qualidade.data.avaliacao.placar.violacoes > 0} contexto="respostas com um termo proibido pelo caso · meta do guia: zero" />
              </FaixaIndicadores>
            </>
          ) : (
            <p className="txt-apoio text-tinta-secundaria">
              Precisão, abstenção correta e recuperação do trecho certo só se medem contra um gabarito: o conjunto de avaliação do guia
              (seção 15.1), com perguntas reais e a resposta esperada. Monte os casos na aba Conjunto de avaliação e rode
              {t ? ` · custo do período: ${formatarInteiro(t.tokens)} tokens` : ''}.
            </p>
          )}

          <div role="tablist" aria-label="Seções da qualidade da IA" ref={fileteDasAbas} className="barra-de-abas filete-das-abas">
            {(
              [
                ['registro', 'Registro da IA'],
                ['avaliacao', 'Conjunto de avaliação'],
              ] as const
            ).map(([id, rotulo]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={aba === id}
                onClick={() => {
                  irPara({ aba: id, pagina: 1 });
                }}
                className="aba"
              >
                {rotulo}
              </button>
            ))}
          </div>

          {aba === 'avaliacao' ? (
            <ConjuntoDeAvaliacao rodadaAberta={rodadaAberta} aoAbrirRodada={setRodadaAberta} />
          ) : (
            <>
          <BarraFiltros
            chips={chips}
            contagem={registro.isPending ? null : `${formatarInteiro(total)} ${total === 1 ? 'saída da IA' : 'saídas da IA'}`}
            aoLimpar={() => {
              irPara({ assunto: '' });
            }}
          >
            <CampoSelecao
              rotulo="Saída da IA"
              valor={assunto ?? ''}
              rotuloVazio="Todas"
              aplicado={Boolean(assunto)}
              opcoes={ASSUNTOS_DA_IA.map((a) => ({ valor: a, rotulo: ROTULO_DO_ASSUNTO[a] }))}
              aoMudar={(novo) => {
                irPara({ assunto: (ASSUNTOS_DA_IA as readonly string[]).includes(novo) ? (novo as AssuntoDaIa) : '' });
              }}
            />
          </BarraFiltros>

          <div data-painel-origem>
            <Tabela
              rotulo="Registro da IA"
              colunas={colunas}
              linhas={itens}
              alturaDupla
              chaveDaLinha={(i) => i.id}
              carregando={registro.isPending}
              selecionada={aberto}
              aoSelecionar={(i) => {
                setAberto(i.id);
              }}
              erro={
                registro.isError ? (
                  <EstadoVazio
                    tom="erro"
                    titulo="Não foi possível carregar o registro"
                    descricao={`${explicarFalha(registro.error).erro}. ${explicarFalha(registro.error).comoResolver}`}
                    acao={<Botao nivel="secundario" onClick={() => { void registro.refetch(); }}>Carregar o registro</Botao>}
                  />
                ) : null
              }
              vazio={
                assunto ? (
                  <EstadoVazio
                    titulo={`Nada registrado em “${ROTULO_DO_ASSUNTO[assunto]}”`}
                    descricao="Escolha outra saída da IA ou limpe o filtro para ver o registro inteiro."
                    acao={<Botao nivel="secundario" onClick={() => { irPara({ assunto: '' }); }}>Limpar filtro</Botao>}
                  />
                ) : (
                  <EstadoVazio
                    icone={Sparkles}
                    titulo="A IA ainda não respondeu nada nesta empresa"
                    descricao="Cada pergunta à máquina, hipótese de O.S., resumo do painel e leitura de folha ou de cupom entra aqui sozinha, com quem pediu e o que a IA disse."
                  />
                )
              }
              rodape={
                total > 0 ? (
                  <RodapeTabela
                    primeiro={(pagina - 1) * porPagina + 1}
                    ultimo={(pagina - 1) * porPagina + itens.length}
                    total={total}
                    pagina={pagina}
                    paginas={paginas}
                    porPagina={porPagina}
                    opcoesPorPagina={[porPagina]}
                    aoMudarPagina={(nova) => {
                      irPara({ pagina: nova });
                    }}
                    aoMudarPorPagina={() => undefined}
                  />
                ) : null
              }
            />
          </div>
            </>
          )}
        </div>
      </div>

      {aba === 'avaliacao' && rodadaAberta ? (
        <DetalheDaRodada
          key={rodadaAberta}
          id={rodadaAberta}
          aoFechar={() => {
            setRodadaAberta(null);
          }}
        />
      ) : null}
      {aba === 'registro' && item ? (
        <DetalheDaSaida
          key={item.id}
          item={item}
          aoFechar={() => {
            setAberto(null);
          }}
        />
      ) : null}
    </>
  );
}

interface Detalhe {
  readonly citacoes: readonly { readonly trechoId: string; readonly titulo: string; readonly pagina: number | null; readonly texto: string }[];
  readonly notas: readonly { readonly util: boolean; readonly motivo: string | null; readonly quem: string | null; readonly em: string }[];
}

/** O que a IA disse por extenso, as fontes que ela citou e as notas. A folha abre pela tela dela. */
function DetalheDaSaida({ item, aoFechar }: { readonly item: ItemDoRegistro; readonly aoFechar: () => void }) {
  const ehFolha = item.assunto === 'folha' || item.assunto === 'folha_de_turno';
  const detalhe = useQuery({
    queryKey: ['ia', 'registro', 'item', item.id],
    enabled: !ehFolha,
    queryFn: () => pedir<Detalhe>(`/ia/registro/${item.id}`),
  });
  return (
    <PainelDetalhe
      titulo={item.equipamento ?? 'Sem máquina'}
      subtitulo={`${ROTULO_DO_ASSUNTO[item.assunto]} · ${formatarDataHora(new Date(item.quando))} · ${item.quem ?? 'Fora do cadastro'}`}
      dadosChave={
        <>
          <DadoChave rotulo="Modelo de IA">{item.modelo ?? 'Sem registro'}</DadoChave>
          <DadoChave rotulo="Tokens">
            {item.tokensEntrada === null ? 'Sem registro' : `${formatarInteiro(item.tokensEntrada)} de entrada · ${formatarInteiro(item.tokensSaida ?? 0)} de saída`}
          </DadoChave>
          <DadoChave rotulo="Tempo">{item.duracaoMs === null ? 'Sem registro' : `${formatarInteiro(item.duracaoMs)} ms`}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'resposta',
          rotulo: 'O que a IA disse',
          conteudo: (
            <div className="flex flex-col gap-3">
              <p className="txt-apoio text-tinta-secundaria">{item.pergunta}</p>
              <p className="txt-corpo text-tinta">{item.resposta}</p>
              {item.falta ? <p className="txt-apoio text-tinta-secundaria">{`O que faltou: ${item.falta}.`}</p> : null}
              {ehFolha ? (
                <p className="txt-apoio text-tinta-secundaria">A leitura da folha, linha a linha, abre pela tela de folhas do módulo.</p>
              ) : (detalhe.data?.citacoes.length ?? 0) > 0 ? (
                <ol className="flex list-decimal flex-col gap-1 pl-5">
                  {(detalhe.data?.citacoes ?? []).map((c) => (
                    <li key={c.trechoId} className="txt-apoio text-tinta-secundaria">
                      <span className="text-tinta">{c.pagina !== null ? `${c.titulo}, página ${String(c.pagina)}` : c.titulo}</span>
                      {` — “${c.texto.length > 220 ? `${c.texto.slice(0, 220)}…` : c.texto}”`}
                    </li>
                  ))}
                </ol>
              ) : null}
            </div>
          ),
        },
        {
          id: 'notas',
          rotulo: 'Serviu?',
          conteudo:
            ehFolha ? (
              <EstadoVazio titulo="A folha não leva nota" descricao="O acerto da leitura é a conferência: cada campo corrigido por quem conferiu conta como erro da IA." />
            ) : (detalhe.data?.notas.length ?? 0) === 0 ? (
              <EstadoVazio titulo="Ninguém avaliou esta resposta" descricao="Só quem pediu a resposta diz se ela serviu, na tela em que ela apareceu." />
            ) : (
              <ul className="flex flex-col gap-2">
                {(detalhe.data?.notas ?? []).map((n) => (
                  <li key={`${n.em}${n.quem ?? ''}`} className="flex flex-col gap-1 border-b border-borda-sutil pb-2">
                    <span className="txt-corpo text-tinta">{n.util ? 'Serviu' : 'Não serviu'}</span>
                    <span className="txt-apoio text-tinta-apagada">{`${n.quem ?? 'Fora do cadastro'} · ${formatarDataHora(new Date(n.em))}`}</span>
                    {n.motivo ? <span className="txt-apoio text-tinta-secundaria">{n.motivo}</span> : null}
                  </li>
                ))}
              </ul>
            ),
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}
